import {
  fetchDiagnosisCodes,
  convertDiagnosisCodes,
  handleFetchDiagnosisCodes,
  type DiagnosisCodeOptions,
  type DiagnosisCodes,
} from '@/entities/study';
import {
  FREQUENTLY_OF_SYMPTOMS_OPTIONS,
  INSURANCE_TYPE_OPTIONS,
  PATIENT_SYMPTOMS_OPTIONS,
} from '@/shared/config';
import { showMessageError, checkNull } from '@/shared/lib';
import { useConstructor, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useImperativeHandle } from 'react';
import { t } from 'ttag';
import type { PopupScrollEvent } from '@/shared/ui/select.types';
import type { NewStudyStep4Props, NewStudyStep4State, Option } from './newStudyStep4.types';

/**
 * Drives the diagnosis, insurance and symptom step of the new-study wizard, holding the step 4 data, the resolved option objects, the paged primary and secondary diagnosis code lists and their loading flags. Seeds itself through createInitialData on every defaultData change, refetches diagnosis codes whenever a debounced search term changes, exposes onClickBottomButtons as handleNavigation on ref, scrolls to the top on mount and clears field errors on any edit.
 *
 * @param defaultData - Previously captured step 4 answers used to prefill the form.
 * @param setIsLoadedData - Callback flagging whether the step has finished seeding its data.
 * @param handleClick - Wizard callback receiving the step4 payload and the direction flag.
 * @param ref - Forwarded ref exposing handleNavigation so the wizard can trigger validation.
 * @returns The step 4 state together with the change, symptom, diagnosis search and dropdown paging handlers.
 */
const useNewStudyStep4 = (
  { defaultData, setIsLoadedData, handleClick }: NewStudyStep4Props,
  ref: any,
) => {
  const [state, setState] = useMergeState<NewStudyStep4State>({
    newStudyStep4Data: {
      primary: undefined,
      insuranceType: undefined,
      physiciansNote: undefined,
      second: undefined,
      frequentlyOfSymptoms: undefined,
      ...defaultData,
      // defaultData may carry these as null (explicitly cleared), which the state's
      // own shape doesn't allow, so normalize null to undefined here.
      insuranceProvider: (defaultData as any).insuranceProvider ?? undefined,
      patientMedications: (defaultData as any).patientMedications ?? undefined,
      usedHeartMonitor: (defaultData as any).usedHeartMonitor ?? undefined,
      // defaultData starts as {} until the wizard has real data, so these two required
      // fields still need their own blank-slate fallback
      insurancePreAuth: (defaultData as any).insurancePreAuth ?? 'true',
      patientSymptoms: (defaultData as any).patientSymptoms ?? [],
    },
    insuranceType: undefined,
    patientSymptoms: undefined,
    frequentlyOfSymptoms: undefined,
    errorObject: {},
    isLoadingPrimary: false,
    searchPrimary: '',
    diagnosisCodesPrimary: [],
    isLoadingSecond: false,
    searchSecond: '',
    diagnosisCodesSecond: [],
  });

  let timeoutSearchPrimary: ReturnType<typeof setTimeout> | undefined;
  let timeoutSearchSecond: ReturnType<typeof setTimeout> | undefined;

  /**
   * Stores the chosen patient symptoms of the step 4 form as the list of their option values. Defaults usedHeartMonitor to true when symptoms exist and no answer was given yet, clears it once the selection is empty, keeps the option objects in state for display and mirrors the merged data back onto defaultData.
   *
   * @param name - Key the symptom value list is written to.
   * @param value - Raw change value emitted by the select, unused for the stored data.
   * @param options - Currently selected symptom options.
   * @returns Nothing.
   */
  const onChangePatientSymptoms = (name: string, value: string, options: Option[]) => {
    const updatedNewStudyStep4Data: any = { ...state.newStudyStep4Data };
    updatedNewStudyStep4Data[name] = _.map(options, (option) => option.value);
    updatedNewStudyStep4Data.usedHeartMonitor = options?.length
      ? !_.isNil(state.newStudyStep4Data.usedHeartMonitor)
        ? state.newStudyStep4Data.usedHeartMonitor
        : true
      : undefined;
    setState({
      [name]: options,
      newStudyStep4Data: updatedNewStudyStep4Data,
    });
    _.assign(defaultData, updatedNewStudyStep4Data);
  };

  /**
   * Writes one diagnosis, insurance or note value into the step 4 data of the new-study wizard. Rejects patientMedications over 200 characters and physiciansNote over 400 by storing a maximum-length message instead, stores an emptied primary or second diagnosis as null while clearing both diagnosis searches and their loaded code lists, and otherwise assigns the value unchanged.
   *
   * @param name - Key inside newStudyStep4Data to write, also used as the error key.
   * @param value - Value entered for that field.
   * @returns Nothing.
   */
  const onChange = (name: string, value: string) => {
    const updatedNewStudyStep4Data: any = { ...state.newStudyStep4Data };

    if (name === 'patientMedications') {
      if (value.length > 200) {
        const newErrorObject = { ...state.errorObject };
        _.assign(newErrorObject, { patientMedications: 'Maximum 200 characters allowed.' });
        setState({ errorObject: newErrorObject });
        return;
      }
    }

    if (name === 'physiciansNote') {
      if (value.length > 400) {
        const newErrorObject = { ...state.errorObject };
        _.assign(newErrorObject, { physiciansNote: 'Maximum 400 characters allowed.' });
        setState({ errorObject: newErrorObject });
        return;
      }
    }

    if (['primary', 'second'].includes(name)) {
      const nullableValue = value || null;
      updatedNewStudyStep4Data[name] = nullableValue;
      setState({
        [name]: nullableValue,
        newStudyStep4Data: updatedNewStudyStep4Data,
        searchPrimary: '',
        diagnosisCodesPrimary: [],
        searchSecond: '',
        diagnosisCodesSecond: [],
      });
      return;
    }

    updatedNewStudyStep4Data[name] = value;
    setState({ newStudyStep4Data: updatedNewStudyStep4Data });
  };

  /**
   * Records a single-select choice such as insurance type or symptom frequency on the step 4 form. Writes option.value into the named field of newStudyStep4Data while keeping the whole option under the same key in state so the select can render it.
   *
   * @param name - Key inside newStudyStep4Data the choice belongs to.
   * @param value - Raw change value emitted by the select, unused for the stored data.
   * @param option - Chosen option whose value is stored in the form data.
   * @returns Nothing.
   */
  const onChangeSelection = (name: string, value: string, option: Option) => {
    const updatedNewStudyStep4Data: any = { ...state.newStudyStep4Data };

    updatedNewStudyStep4Data[name] = option?.value;

    setState({
      [name]: option,
      newStudyStep4Data: updatedNewStudyStep4Data,
    });
  };

  /**
   * Debounces the primary diagnosis code search of the step 4 form by 500 ms. Cancels any pending timer, then stores a non-empty term as searchPrimary or resets both searchPrimary and the already loaded primary code list.
   *
   * @param value - Text typed into the primary diagnosis code select.
   * @returns Nothing.
   */
  const handleSearchSelectPrimary = (value: string) => {
    clearTimeout(timeoutSearchPrimary);
    timeoutSearchPrimary = setTimeout(() => {
      if (!_.isEmpty(value)) {
        setState({ searchPrimary: value });
      } else {
        setState({
          searchPrimary: '',
          diagnosisCodesPrimary: [],
        });
      }
    }, 500);
  };

  /**
   * Debounces the secondary diagnosis code search of the step 4 form by 500 ms. Cancels any pending timer, then stores a non-empty term as searchSecond or resets both searchSecond and the already loaded secondary code list.
   *
   * @param value - Text typed into the secondary diagnosis code select.
   * @returns Nothing.
   */
  const handleSearchSelectSecond = (value: string) => {
    clearTimeout(timeoutSearchSecond);
    timeoutSearchSecond = setTimeout(() => {
      if (!_.isEmpty(value)) {
        setState({ searchSecond: value });
      } else {
        setState({
          searchSecond: '',
          diagnosisCodesSecond: [],
        });
      }
    }, 500);
  };

  /**
   * Appends the next page of primary diagnosis codes when its dropdown reaches the bottom. Compares scrollTop plus clientHeight against scrollHeight, raises isLoadingPrimary, requests more codes from handleFetchDiagnosisCodes in load-more mode with the current search terms, and merges the returned lists back into state.
   *
   * @param e - Dropdown scroll event whose target supplies the scroll offsets.
   * @returns A promise settling once the appended options and the cleared loading flag are in state.
   */
  const handlePopupScrollPrimary = async (e: PopupScrollEvent) => {
    const { target } = e;
    if (target.scrollTop + target.clientHeight === target.scrollHeight) {
      setState({ isLoadingPrimary: true });
      const newState = await handleFetchDiagnosisCodes({
        type: 'primary',
        isLoadMore: true,
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({
        ...newState,
        isLoadingPrimary: false,
      });
    }
  };

  /**
   * Appends the next page of secondary diagnosis codes when its dropdown reaches the bottom. Compares scrollTop plus clientHeight against scrollHeight, raises isLoadingSecond, requests more codes from handleFetchDiagnosisCodes in load-more mode with the current search terms, and merges the returned lists back into state.
   *
   * @param e - Dropdown scroll event whose target supplies the scroll offsets.
   * @returns A promise settling once the appended options and the cleared loading flag are in state.
   */
  const handlePopupScrollSecond = async (e: PopupScrollEvent) => {
    const { target } = e;
    if (target.scrollTop + target.clientHeight === target.scrollHeight) {
      setState({ isLoadingSecond: true });
      const newState = await handleFetchDiagnosisCodes({
        type: 'second',
        isLoadMore: true,
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({
        ...newState,
        isLoadingSecond: false,
      });
    }
  };

  /**
   * Validates step 4 before handing its answers to the wizard, or steps back without checking. Advancing requires a primary diagnosis code and an insurance type, plus a symptom frequency checked through checkNull once symptoms were selected; any error shows the invalid-information toast and stores the field messages, otherwise the step4 payload goes to handleClick.
   *
   * @param isNextClicked - True when advancing, which triggers validation, false when stepping back.
   * @returns Nothing.
   */
  const onClickBottomButtons = (isNextClicked: boolean) => {
    if (isNextClicked) {
      const errorObject = {};

      if (state.newStudyStep4Data.patientSymptoms?.length) {
        const checkNullData = [
          {
            field: 'frequentlyOfSymptoms',
            value: state.newStudyStep4Data.frequentlyOfSymptoms,
            isRequired: true,
          },
        ];
        const frequentlyOfSymptomsError = checkNull(checkNullData);
        _.assign(errorObject, { ...frequentlyOfSymptomsError });
      }

      if (!state.newStudyStep4Data.primary) {
        _.assign(errorObject, { primary: 'Invalid value' });
      }

      if (!state.newStudyStep4Data.insuranceType) {
        _.assign(errorObject, { insuranceType: 'Invalid value' });
      }

      if (!_.isEmpty(errorObject)) {
        showMessageError({
          content: t`Some information is invalid. Please review the highlighted field(s).`,
        });
        setState({ errorObject });
      } else {
        handleClick({ step4: state.newStudyStep4Data }, true);
      }
    } else {
      handleClick({ step4: state.newStudyStep4Data }, false);
    }
  };

  /**
   * Seeds the step 4 diagnosis, insurance and symptom form of the new-study wizard from previously captured data. Fetches diagnosis codes for the stored primary and secondary values in parallel and keeps the option whose code the stored value contains, resolves insurance type, each patient symptom and the symptom frequency against INSURANCE_TYPE_OPTIONS, PATIENT_SYMPTOMS_OPTIONS and FREQUENTLY_OF_SYMPTOMS_OPTIONS, then commits everything and reports the step as loaded.
   *
   * @returns A promise settling once the resolved options and form data have been committed.
   */
  const createInitialData = async () => {
    const updatedNewStudyStep4Data = { ...state.newStudyStep4Data };
    _.merge(updatedNewStudyStep4Data, defaultData);

    const setStateObject = {};

    const promises = [];
    if (defaultData.primary) {
      promises.push(
        fetchDiagnosisCodes({
          searchDiagnosisCode: defaultData.primary as string,
        }),
      );
    }
    if (defaultData.second) {
      promises.push(
        fetchDiagnosisCodes({
          searchDiagnosisCode: defaultData.second as string,
        }),
      );
    }
    if (promises.length > 0) {
      const [primaryDiagnosisCodes, secondDiagnosisCodes] = await Promise.all(promises);
      if ((primaryDiagnosisCodes?.length ?? 0) > 0) {
        /**
         * The primary diagnosis option whose code is contained in the primary value carried by the incoming step data. Selected from the fetched primary diagnosis codes after they are converted to select options by convertDiagnosisCodes.
         *
         * @returns The matching primary diagnosis option, or undefined when none of the fetched codes is contained in the stored value.
         */
        // GraphQL codegen types id/fullDisplay as nullable; the query never actually returns nulls here
        const defaultPrimary = _.find(
          convertDiagnosisCodes(primaryDiagnosisCodes as DiagnosisCodes[] | null | undefined),
          (x) => (defaultData.primary as string).includes(x.value),
        );
        updatedNewStudyStep4Data.primary = (defaultPrimary as DiagnosisCodeOptions)?.value;
      }
      if ((secondDiagnosisCodes?.length ?? 0) > 0) {
        /**
         * The secondary diagnosis option whose code is contained in the second value carried by the incoming step data. Selected from the fetched secondary diagnosis codes after they are converted to select options by convertDiagnosisCodes.
         *
         * @returns The matching secondary diagnosis option, or undefined when none of the fetched codes is contained in the stored value.
         */
        // GraphQL codegen types id/fullDisplay as nullable; the query never actually returns nulls here
        const defaultSecond = _.find(
          convertDiagnosisCodes(secondDiagnosisCodes as DiagnosisCodes[] | null | undefined),
          (x) => (defaultData.second as string).includes(x.value),
        );
        updatedNewStudyStep4Data.second = (defaultSecond as DiagnosisCodeOptions)?.value;
      }
    }

    /**
     * The insurance type option matching the insurance type stored in the incoming step data. Looked up in INSURANCE_TYPE_OPTIONS by comparing each option value to defaultData.insuranceType.
     *
     * @returns The matching insurance type option, or undefined when nothing matches.
     */
    const defaultInsurance = _.find(
      INSURANCE_TYPE_OPTIONS,
      (x) => x.value === defaultData.insuranceType,
    );
    _.assign(setStateObject, { insuranceType: defaultInsurance });

    const patientSymptoms: Option[] = [];
    _.forEach(defaultData.patientSymptoms as any[], (patientSymptom) => {
      /**
       * The patient symptom option matching one stored symptom code. Looked up in PATIENT_SYMPTOMS_OPTIONS by value so a persisted code can be rendered as its select option.
       *
       * @returns The matching symptom option, or undefined when the code is not in the list.
       */
      const defaultPatientSymptom = _.find(
        PATIENT_SYMPTOMS_OPTIONS,
        (x) => x.value === patientSymptom,
      );
      if (defaultPatientSymptom) {
        patientSymptoms.push(defaultPatientSymptom);
      }
    });
    _.assign(setStateObject, {
      patientSymptoms: patientSymptoms.length ? patientSymptoms : undefined,
    });

    /**
     * The symptom-frequency option matching the frequency stored in the incoming step data. Looked up in FREQUENTLY_OF_SYMPTOMS_OPTIONS by comparing each option value to defaultData.frequentlyOfSymptoms.
     *
     * @returns The matching frequency option, or undefined when nothing matches.
     */
    const defaultFrequentlyOfSymptoms = _.find(
      FREQUENTLY_OF_SYMPTOMS_OPTIONS,
      (x) => x.value === defaultData.frequentlyOfSymptoms,
    );
    _.assign(setStateObject, { frequentlyOfSymptoms: defaultFrequentlyOfSymptoms });

    _.assign(setStateObject, {
      newStudyStep4Data: updatedNewStudyStep4Data,
    });

    setState(setStateObject);
    setIsLoadedData(true);
  };

  useImperativeHandle(ref, () => ({
    handleNavigation: onClickBottomButtons,
  }));

  useConstructor(() => {
    setIsLoadedData(false);
  });

  useEffect(() => {
    createInitialData();
  }, [defaultData]);

  useUpdateEffect(() => {
    if (!_.isEmpty(state.errorObject)) {
      setState({ errorObject: {} });
    }
  }, [state.newStudyStep4Data]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const bodyElement = document.querySelector('.app-body');
    if (bodyElement) {
      bodyElement.scroll(0, 0);
    }
  }, []);

  useUpdateEffect(async () => {
    if (!_.isEmpty(state.searchPrimary)) {
      setState({
        isLoadingPrimary: true,
        diagnosisCodesPrimary: [],
      });
      const newState = await handleFetchDiagnosisCodes({
        type: 'primary',
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({
        ...newState,
        isLoadingPrimary: false,
      });
    }
  }, [state.searchPrimary]);

  useUpdateEffect(async () => {
    if (!_.isEmpty(state.searchSecond)) {
      setState({
        isLoadingSecond: true,
        diagnosisCodesSecond: [],
      });
      const newState = await handleFetchDiagnosisCodes({
        type: 'second',
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({
        ...newState,
        isLoadingSecond: false,
      });
    }
  }, [state.searchSecond]);

  return {
    state,
    onChangePatientSymptoms,
    onChange,
    onChangeSelection,
    handleSearchSelectPrimary,
    handleSearchSelectSecond,
    handlePopupScrollPrimary,
    handlePopupScrollSecond,
  };
};

export { useNewStudyStep4 };
